// ── Athena Notification System ────────────────────────────────────────────────
// Central module for all notification logic.
// Web limitations vs future native app are documented inline.

import { useState, useEffect } from 'react';
import { API_BASE, IS_NATIVE_APP } from '../config/api';
import {
  requestAthenaNotificationsPermission,
  sanitizeAthenaNotificationRoute,
} from '../notifications/AthenaNotifications';

export const ATHENA_NOTIF_ICON = '/athena-favicon.png';

// ── Preferences ───────────────────────────────────────────────────────────────
export const DEFAULT_NOTIF_PREFS = {
  enabled:    true,
  frequency:  'Normal',  // Baixa | Normal | Alta
  morning:    true,      // ~06:00
  afternoon:  true,      // ~13:00
  evening:    true,      // ~21:00
};

export function loadNotifPrefs() {
  try {
    return { ...DEFAULT_NOTIF_PREFS, ...JSON.parse(localStorage.getItem('athena_notif_prefs') || '{}') };
  } catch { return { ...DEFAULT_NOTIF_PREFS }; }
}

export function saveNotifPrefs(prefs) {
  localStorage.setItem('athena_notif_prefs', JSON.stringify({ ...DEFAULT_NOTIF_PREFS, ...prefs }));
}

// ── Send notification ─────────────────────────────────────────────────────────
// Uses Service Worker (richer, stays after tab closes) when available,
// falls back to direct Notification API.
//
// NATIVE APP FUTURE:
//   - Custom sound requires native Android/iOS app
//   - Inline reply (WhatsApp-style) requires native app
//   - Persistent notifications (app closed) require push subscription + backend scheduler
//   - Rich media (images in notification) requires native
export function getCurrentGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return { period: 'manha', greeting: 'Bom dia',    hour: h };
  if (h >= 12 && h < 18) return { period: 'tarde', greeting: 'Boa tarde', hour: h };
  return { period: 'noite', greeting: 'Boa noite', hour: h };
}

export function sendAthenaNotification({ title = 'Athena', body = '', tag = 'athena', data = {} } = {}) {
  // A Fase 1 mantém as notificações web restritas à PWA. No shell Android o
  // agendamento continua no backend, mas a entrega visual aguarda a integração
  // de Push Notifications nativa, para não simular uma permissão inexistente.
  if (IS_NATIVE_APP) return false;
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    // A Athena remove service workers legados para evitar cache desatualizado.
    // Usar a API direta impede que navigator.serviceWorker.ready fique pendente
    // e faz a notificação aparecer imediatamente com o app aberto/instalado.
    new Notification(title, { body, tag, icon: ATHENA_NOTIF_ICON, data });
    addNotifLogEntry({ title, body, tag });
    return true;
  } catch { return false; }
}

export async function requestAthenaNotificationPermission() {
  return requestAthenaNotificationsPermission();
}

// ── Notification log (workspace bell panel) ────────────────────────────────────
// Every notification that actually fires above gets logged here, so the
// workspace bell can show a real unread count/list instead of a static number.
// Scoped per user with the same JWT-decode pattern used elsewhere in the app
// for user-scoped localStorage keys (e.g. pinned sessions).
const NOTIF_LOG_MAX = 30;
const NOTIF_LOG_EVENT = 'athena-notif-log-changed';

function notifLogKey() {
  try {
    const tok = localStorage.getItem('athena_token');
    if (!tok) return 'athena_notif_log';
    const p = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return p.sub ? `athena_notif_log_${p.sub}` : 'athena_notif_log';
  } catch { return 'athena_notif_log'; }
}

export function loadNotifLog() {
  try { return JSON.parse(localStorage.getItem(notifLogKey()) || '[]'); }
  catch { return []; }
}

function saveNotifLog(log) {
  localStorage.setItem(notifLogKey(), JSON.stringify(log.slice(0, NOTIF_LOG_MAX)));
  window.dispatchEvent(new CustomEvent(NOTIF_LOG_EVENT));
}

export function addNotifLogEntry({ id = null, title = 'Athena', body = '', tag = 'athena', route = null, source = 'local' } = {}) {
  const safeId = typeof id === 'string' && /^[A-Za-z0-9._:-]{1,160}$/.test(id)
    ? id
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id: safeId,
    title,
    body,
    tag,
    route: sanitizeAthenaNotificationRoute(route),
    createdAt: Date.now(),
    read: false,
    source,
  };
  const existing = loadNotifLog();
  const previous = existing.find((item) => item.id === safeId);
  const next = previous
    ? [{ ...previous, ...entry, read: previous.read, clicked: previous.clicked }, ...existing.filter((item) => item.id !== safeId)]
    : [entry, ...existing];
  saveNotifLog(next);
  return next[0];
}

export function markNotifRead(id) {
  saveNotifLog(loadNotifLog().map(n => (n.id === id ? { ...n, read: true } : n)));
  const token = localStorage.getItem('athena_token');
  if (token && id) fetch(`${API_BASE}/notifications/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ action: 'read' }) }).catch(() => {});
}

/** A notification tap is distinct from merely reading the notification bell. */
export function markNotifClicked(id) {
  saveNotifLog(loadNotifLog().map(n => (n.id === id ? { ...n, read: true, clicked: true } : n)));
  const token = localStorage.getItem('athena_token');
  if (token && id) {
    fetch(`${API_BASE}/notifications/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'clicked' }),
    }).catch(() => {});
  }
}

export function markAllNotifRead() {
  saveNotifLog(loadNotifLog().map(n => ({ ...n, read: true })));
  const token = localStorage.getItem('athena_token');
  if (token) fetch(`${API_BASE}/notifications/read-all`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
}

async function syncNotificationLog() {
  const token = localStorage.getItem('athena_token');
  if (!token) return;
  const response = await fetch(`${API_BASE}/notifications?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) return;
  const data = await response.json();
  const items = (data.notifications || []).map((entry) => ({
    id: entry.id,
    title: entry.title,
    body: entry.body,
    tag: entry.type || 'athena',
    createdAt: Date.parse(entry.created_at) || Date.now(),
    read: !!entry.read_at,
    clicked: !!entry.clicked_at,
    route: sanitizeAthenaNotificationRoute(entry.route),
    source: 'server',
  }));
  // A foreground native event is visible immediately, even if the Worker is
  // briefly offline or its notification row has not replicated yet. Once its
  // server ID appears, the server record replaces this local bridge entry.
  const serverIds = new Set(items.map((entry) => entry.id));
  const maxForegroundAge = 6 * 60 * 60 * 1000;
  const localForeground = loadNotifLog().filter((entry) => (
    entry.source === 'foreground'
    && !serverIds.has(entry.id)
    && Date.now() - Number(entry.createdAt || 0) < maxForegroundAge
  ));
  saveNotifLog([...items, ...localForeground].sort((a, b) => Number(b.createdAt) - Number(a.createdAt)));
}

// Live hook for the workspace bell — real, reactive unread count + list.
export function useNotifLog() {
  const [log, setLog] = useState(loadNotifLog);
  useEffect(() => {
    const onChange = () => setLog(loadNotifLog());
    const sync = () => syncNotificationLog().catch(() => {});
    const onForeground = (event) => {
      const notification = event?.detail;
      if (notification && (notification.id || notification.title || notification.body)) {
        addNotifLogEntry({
          id: notification.id || null,
          title: notification.title || 'Athena',
          body: notification.body || '',
          tag: notification.type || 'athena',
          route: notification.route || null,
          source: 'foreground',
        });
      }
      sync();
    };
    sync();
    window.addEventListener(NOTIF_LOG_EVENT, onChange);
    window.addEventListener('storage', onChange);
    window.addEventListener('athena-push-foreground', onForeground);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener(NOTIF_LOG_EVENT, onChange);
      window.removeEventListener('storage', onChange);
      window.removeEventListener('athena-push-foreground', onForeground);
      window.removeEventListener('focus', sync);
    };
  }, []);
  return { log, unreadCount: log.filter(n => !n.read).length };
}

// ── Extract notification command from LLM response ────────────────────────────
// Handles all variants: [NOTIFICATION:], [NOTIFICAÇÃO:], [NÓTIFICAÇÃO:]
// Returns { notifBody: string|null, cleanText: string }
const _NTAG   = /\[(?:N[OÓ]TIFICA[CÇ][AÃ]O|NOTIFICATION):\s*([^\]|]{1,200}?)(?:\|[^\]]*)?\s*\]/i;
const _NTAG_G = /\s*\[(?:N[OÓ]TIFICA[CÇ][AÃ]O|NOTIFICATION):[^\]]+\]\s*/gi;

export function extractNotificationCommand(text) {
  if (!text) return { notifBody: null, cleanText: text };
  const match = _NTAG.exec(text);
  if (!match) return { notifBody: null, cleanText: text };
  const notifBody = match[1].trim();
  const cleanText = text.replace(_NTAG_G, ' ').trim();
  return { notifBody, cleanText };
}

// Legacy alias for backward compat
export const parseNotifFromResponse = (text) => extractNotificationCommand(text);

// ── Contextual notification generator ─────────────────────────────────────────
// Generates a time-appropriate notification based on context.
// @param {{ userName, lastSummary }} ctx
// @returns {{ title, body, tag }|null}
export function getContextualNotification({ userName = 'Vitor', lastSummary = '' } = {}) {
  const prefs = loadNotifPrefs();
  if (!prefs.enabled) return null;

  const h = new Date().getHours();

  if (h >= 6 && h < 9 && prefs.morning) {
    const body = lastSummary
      ? `Bom dia, ${userName}! ☕ ${lastSummary}`
      : `Bom dia, ${userName}! ☀️ Pronto para mais um dia?`;
    return { title: 'Athena', body: body.slice(0, 120), tag: 'bom-dia' };
  }
  if (h >= 13 && h < 15 && prefs.afternoon) {
    return { title: 'Athena', body: `Boa tarde, ${userName}! 🌤️ Como está indo o dia?`, tag: 'boa-tarde' };
  }
  if ((h >= 21 || h < 1) && prefs.evening) {
    return { title: 'Athena', body: `Boa noite! 🌙 Precisa de algo antes de encerrar?`, tag: 'boa-noite' };
  }
  return null;
}

// ── Contextual notification scheduler ─────────────────────────────────────────
// Runs while the app is open. For persistent notifications (app closed),
// a backend scheduler + push subscription (Web Push API) is needed.
// @param {{ userName, getLastSummary }} opts
// @returns {function} cleanup
export function startContextualScheduler({ userName = 'Vitor', getLastSummary = () => '' } = {}) {
  // A presença proativa passou a ser decidida no Worker com contexto, fuso,
  // cooldown e histórico. Não criamos notificações locais estáticas enquanto
  // a página está aberta, pois isso duplicaria o push de background.
  void userName; void getLastSummary;
  return () => {};
}

// ── FUTURE NATIVE APP STRUCTURE ────────────────────────────────────────────────
// These features are architecturally prepared but require a native app:
//
// pushSubscribe()     — subscribe to Web Push for backend-initiated notifications
// pushUnsubscribe()   — remove push subscription
// scheduleReminder()  — persistent reminder via backend scheduler
// cancelReminder()    — cancel scheduled reminder
// setNotifSound()     — custom sound (requires native)
// sendRichNotif()     — image/GIF in notification (requires native)
// enableInlineReply() — reply from notification without opening app (requires native)
//
// Implementation path for Android:
//   1. Backend: POST /api/push/subscribe (save VAPID subscription)
//   2. Backend: scheduler service using APScheduler or Celery
//   3. Wrap app in Capacitor or expose via TWA (Trusted Web Activity)
//   4. Register FCM (Firebase Cloud Messaging) token for full native access
